import { useNavigate } from 'react-router';
import { Info } from 'lucide-react';
import { emit } from '@/lib/analytics';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/contexts/workspace-context';
import { orgUrl } from '@/lib/org-url';
import { isValidPublicId } from '@/lib/public-id';

interface PlanFloorBannerProps {
  floorHours: number;
  workspacePublicId: string;
  scopeKind: 'channel' | 'sandbox-session';
}

/**
 * Phase 131 -- shown on free-plan workspaces (24h floor) so users see why
 * the list ends where it does and how to upgrade. Hidden on paid plans
 * where floorHours is 168.
 *
 * Captures `delivery_upgrade_cta_clicked` (spec §8.10) when the upgrade
 * button is clicked.
 */
export function PlanFloorBanner({
  floorHours,
  workspacePublicId,
  scopeKind,
}: PlanFloorBannerProps) {
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  if (floorHours >= 168) return null;
  function handleUpgrade() {
    emit(
      'delivery_upgrade_cta_clicked',
      { scope_kind: scopeKind, floor_hours: floorHours },
      { groups: { workspace: workspacePublicId } },
    );
    // R3 -- billing is org-only.
    const orgId = activeWorkspace?.organizationPublicId;
    navigate(orgId && isValidPublicId(orgId, 'org') ? orgUrl(orgId, 'billing') : '/');
  }
  return (
    <Alert className="w-fit max-w-full items-start border-blue-500/30 bg-blue-500/5 sm:items-center [&>svg]:translate-y-0.5 [&>svg]:row-span-1 sm:[&>svg]:translate-y-0">
      <Info className="text-blue-500" />
      <AlertDescription className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
        <span>
          You&rsquo;re seeing the last 24 hours of deliveries. Upgrade your plan to see up to 7
          days.
        </span>
        <Button size="sm" onClick={handleUpgrade}>
          Upgrade Plan
        </Button>
      </AlertDescription>
    </Alert>
  );
}
